import React from 'react';
import styles from './styles.module.scss';
import classNames from 'classnames';
import PageContent from 'src/Components/PageContent';
import ErrorView from 'src/Components/ErrorViewV2';
import { ErrorViewType } from 'src/Components/ErrorViewV2/config';
import { Breakpoint, useBreakpoint } from 'src/utils/breakpoints';
import CosmeticCard from '../../../CosmeticCard';
import PaginationControl from 'src/Components/PaginationControl_V2';
import { useTypedDispatch, useTypedSelector } from 'src/redux/utils';
import { fetchCosmeticTraded } from 'src/redux/actions';
import { AppError } from 'src/redux/types';
import { makeSelectCurrentItemData } from './selectors';
import { navigateToCosmeticDetail } from 'src/utils/navigate';
import EmptyListV2 from 'src/Components/EmptyListV2';
import ItemCardPlaceholder from 'src/Pages/HomePage/Components/ItemCardPlaceholder';
import Skeleton from 'src/Components/Skeleton';

const CosmeticListTab = ({
  className
}) => {
  const breakpoint = useBreakpoint();
  const data = useTypedSelector(makeSelectCurrentItemData());
  const dispatch = useTypedDispatch();
  const [page, setPage] = React.useState(1);
  const {
    totalItems,
    loading,
    invalidated,
    error
  } = useTypedSelector(state => state.cosmeticTraded);
  const invalidatedCosmeticTraded = invalidated && !data;
  const {
    listItem
  } = data || {};
  const itemPerPage = React.useMemo(() => {
    switch (breakpoint) {
      case Breakpoint.PHONE:
        return 8;

      case Breakpoint.TABLET:
        return 9;

      default:
        return 10;
    }
  }, [breakpoint]);
  const itemPerPageRef = React.useRef(itemPerPage);
  const totalPages = Math.ceil(totalItems / itemPerPage) || 1;
  const onFetch = React.useCallback((size, page) => {
    dispatch(fetchCosmeticTraded(size, (page - 1) * size));
  }, [dispatch]);

  const renderLoading = () => {
    return /*#__PURE__*/React.createElement("div", {
      className: classNames(styles.container, className)
    }, /*#__PURE__*/React.createElement("div", {
      className: styles.list
    }, Array(itemPerPage).fill(0).map((_, index) => /*#__PURE__*/React.createElement(ItemCardPlaceholder, {
      key: index,
      className: styles.item
    }))), /*#__PURE__*/React.createElement("div", {
      className: styles.skeletonContainer
    }, /*#__PURE__*/React.createElement(Skeleton, {
      className: styles.paginationSkeleton
    })));
  };

  React.useEffect(() => {
    if (itemPerPage !== itemPerPageRef.current) {
      setPage(1);
      onFetch(itemPerPage, page);
      itemPerPageRef.current = itemPerPage;
    } else {
      onFetch(itemPerPage, page);
    }
  }, [itemPerPage, onFetch, page]);
  React.useEffect(() => {
    let timeout;

    if (data !== null && data !== void 0 && data.timeout) {
      timeout = setTimeout(() => {
        onFetch(itemPerPage, page);
      }, data.timeout * 1000);
    }

    return () => {
      clearInterval(timeout);
    };
  }, [data, dispatch, itemPerPage, page, onFetch]);

  const renderError = () => /*#__PURE__*/React.createElement(ErrorView, {
    config: {
      title: 'Sorry, something went wrong.',
      content: 'Please try again.',
      type: ErrorViewType.Custom
    },
    className: styles.empty
  });

  const renderContent = () => {
    if (!listItem) {
      return null;
    }

    if (listItem.length <= 0) {
      return /*#__PURE__*/React.createElement(EmptyListV2, null);
    }

    return /*#__PURE__*/React.createElement("div", {
      className: classNames(styles.container, className)
    }, /*#__PURE__*/React.createElement("div", {
      className: styles.list
    }, listItem.map(e => /*#__PURE__*/React.createElement(CosmeticCard, {
      className: styles.item,
      cosmetic: e,
      key: `${e.id}${e.timestamp}`,
      onPress: () => {
        navigateToCosmeticDetail(e.refId, {
          isFromHome: true
        });
      }
    }))), /*#__PURE__*/React.createElement(PaginationControl, {
      onPageChange: nextPage => {
        setPage(nextPage);
      },
      page: page,
      totalPages: totalPages
    }));
  };

  return /*#__PURE__*/React.createElement(PageContent, {
    invalidated: invalidatedCosmeticTraded,
    loading: loading,
    error: error != AppError.NO_ERROR,
    renderContent: renderContent,
    renderLoading: renderLoading,
    renderError: renderError
  });
};

export default CosmeticListTab;